// ============================================================================
// STUDIO BOOKINGS SERVICE
// Frontend API client for advance studio room booking + approval workflow
// ============================================================================

import { api } from './api';

// ============================================================================
// TYPES
// ============================================================================

export type BookingDateType = 'morning' | 'afternoon' | 'full_day' | 'multi_day';
export type BookingBillingType = 'investment' | 'chargeable' | 'na';
export type CustomerStatus = 'existing' | 'new' | 'internal';
export type BookingStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';
export type FacilitatorTier = 'tier1' | 'tier2';

export interface StudioBooking {
  bookingId: string;
  roomId: string;
  roomName: string;
  requestedBy: string;
  requestedByName: string;
  requestedByEmail: string;
  facilitatorId: string;
  facilitatorName: string;
  facilitatorTier: FacilitatorTier;
  customer: string;
  eventTypeId: string;
  eventTypeName: string;
  isNewCustomer: boolean;
  customerStatus: CustomerStatus;
  billingType: BookingBillingType;
  dateType: BookingDateType;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  goal: string;
  numberOfCustomers?: number;
  numberOfAttendees?: number;
  status: BookingStatus;
  reviewedBy?: string;
  reviewedByName?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  cancelledBy?: string;
  cancelledAt?: string;
  outlookEventId?: string;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
}

export interface EventType {
  eventTypeId: string;
  name: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
}

export interface FacilitatorSummary {
  userId: string;
  displayName: string;
  email: string;
  tier: FacilitatorTier;
  certifiedAt?: string;
}

export interface ConflictInfo {
  bookingId: string;
  customer: string;
  status: BookingStatus;
  startDate: string;
  endDate: string;
  dateType: BookingDateType;
}

export interface CreateBookingInput {
  roomId: string;
  facilitatorId: string;
  facilitatorName: string;
  facilitatorTier: FacilitatorTier;
  customer: string;
  eventTypeId: string;
  isNewCustomer: boolean;
  customerStatus: CustomerStatus;
  billingType: BookingBillingType;
  dateType: BookingDateType;
  startDate: string;
  endDate: string;
  goal: string;
  numberOfCustomers?: number;
  numberOfAttendees?: number;
}

// Human-readable slot labels and times
export const DATE_TYPE_LABELS: Record<BookingDateType, string> = {
  morning:   'Morning (09:00–12:30)',
  afternoon: 'Afternoon (13:30–17:00)',
  full_day:  'Full Day (09:00–17:00)',
  multi_day: 'Multi-day'
};

export const STATUS_LABELS: Record<BookingStatus, string> = {
  pending:   'Pending Review',
  approved:  'Approved',
  rejected:  'Rejected',
  cancelled: 'Cancelled'
};

export const STATUS_COLORS: Record<BookingStatus, string> = {
  pending:   'text-amber-600 bg-amber-50 border-amber-200',
  approved:  'text-green-700 bg-green-50 border-green-200',
  rejected:  'text-red-700 bg-red-50 border-red-200',
  cancelled: 'text-gray-500 bg-gray-50 border-gray-200'
};

export const TIER_LABELS: Record<FacilitatorTier, string> = {
  tier1: 'Tier 1',
  tier2: 'Tier 2'
};

// ============================================================================
// BOOKING API
// ============================================================================

export async function createBooking(input: CreateBookingInput): Promise<StudioBooking> {
  const res = await api.post('/api/studio/bookings', input);
  return res.data.booking;
}

export async function listMyBookings(): Promise<StudioBooking[]> {
  const res = await api.get('/api/studio/bookings/my');
  return res.data.bookings;
}

export async function cancelBooking(bookingId: string): Promise<StudioBooking> {
  const res = await api.patch(`/api/studio/bookings/${bookingId}/cancel`);
  return res.data.booking;
}

export async function listReviewQueue(): Promise<StudioBooking[]> {
  const res = await api.get('/api/studio/bookings/review');
  return res.data.bookings;
}

export async function listAllBookings(): Promise<StudioBooking[]> {
  const res = await api.get('/api/studio/bookings/all');
  return res.data.bookings;
}

export async function getPendingCount(): Promise<number> {
  const res = await api.get('/api/studio/bookings/pending-count');
  return res.data.count;
}

export async function updateBookingMetadata(
  bookingId: string,
  updates: { numberOfCustomers?: number; numberOfAttendees?: number }
): Promise<StudioBooking> {
  const res = await api.patch(`/api/studio/bookings/${bookingId}/metadata`, updates);
  return res.data.booking;
}

export async function reviewBooking(
  bookingId: string,
  action: 'approved' | 'rejected',
  notes?: string
): Promise<StudioBooking> {
  const res = await api.patch(`/api/studio/bookings/${bookingId}/review`, { action, notes });
  return res.data.booking;
}

// ============================================================================
// EVENT TYPES API
// ============================================================================

export async function listEventTypes(): Promise<EventType[]> {
  const res = await api.get('/api/studio/event-types');
  return res.data.eventTypes;
}

export async function listAllEventTypes(): Promise<EventType[]> {
  const res = await api.get('/api/studio/event-types/all');
  return res.data.eventTypes;
}

export async function createEventType(name: string, description?: string): Promise<EventType> {
  const res = await api.post('/api/studio/event-types', { name, description });
  return res.data.eventType;
}

export async function updateEventType(
  eventTypeId: string,
  updates: Partial<Pick<EventType, 'name' | 'description' | 'isActive'>>
): Promise<EventType> {
  const res = await api.put(`/api/studio/event-types/${eventTypeId}`, updates);
  return res.data.eventType;
}

export async function deleteEventType(eventTypeId: string): Promise<void> {
  await api.delete(`/api/studio/event-types/${eventTypeId}`);
}

// ============================================================================
// FACILITATORS API
// ============================================================================

export async function listFacilitators(): Promise<FacilitatorSummary[]> {
  const res = await api.get('/api/studio/facilitators');
  return res.data.facilitators;
}

export async function certifyUser(userId: string, tier: FacilitatorTier): Promise<void> {
  await api.post(`/api/studio/facilitators/${userId}/certify`, { tier });
}

export async function revokeUserCertification(userId: string): Promise<void> {
  await api.delete(`/api/studio/facilitators/${userId}/certify`);
}

// ============================================================================
// BLOCKED DAYS API
// ============================================================================

export interface BlockedDay {
  date: string;       // YYYY-MM-DD
  reason?: string;
  organizationId: string;
  createdBy: string;
  createdByName: string;
  createdAt: string;
}

export async function listBlockedDays(year: number, month: number): Promise<BlockedDay[]> {
  const res = await api.get('/api/studio/blocked-days', { params: { year, month } });
  return res.data.blockedDays;
}

export async function setBlockedDay(date: string, reason?: string): Promise<void> {
  await api.put(`/api/studio/blocked-days/${date}`, { reason });
}

export async function removeBlockedDay(date: string): Promise<void> {
  await api.delete(`/api/studio/blocked-days/${date}`);
}
